import { rangeIndexToLabel, rangeLabelToIndex } from "../../../components/process-design/cell/Utils";
import { containsFormula, getColumnFromMatrix, getMatrixOfValue, unifyRanges } from "../../../utils/util";
import { hfInstance } from "../../Cells";
import { FillHelper } from "../FillHelpers";
import Content from "./Content";
import Style from "./Style";
const utils = (context) => {
  const { rootState, currentSheet, current } = context;

  return {
    setDefaultHfBehavior: (initialRange, newRange) => {
      let series = hfInstance.getFillRangeData(
        {
          start: { sheet: currentSheet.sheetId, col: initialRange[0], row: initialRange[1] },
          end: { sheet: currentSheet.sheetId, col: initialRange[2], row: initialRange[3] },
        },
        {
          start: { sheet: currentSheet.sheetId, col: newRange[0], row: newRange[1] },
          end: { sheet: currentSheet.sheetId, col: newRange[2], row: newRange[3] },
        }
      );
      return series;
    },
    isRowOrColumnMerged: (range, seriesIn, stepValue, stopValue) => {
      const { rowMap, columnMap } = current.design;
      let activeCellId = rootState.cells.activeCellId;

      // Split activeCellId to get rowId and columnId
      const [activeRowId, activeColumnId] = activeCellId.split("_");
      // Check if rowId or columnId is in mergedCellIds
      return Object.keys(rootState.cells.mergedCellIds).some((mergedId) => {
        const [mergedRowId, mergedColumnId] = mergedId.split("_");
        const startRow = rowMap[mergedRowId].index;
        const startColumn = columnMap[mergedColumnId].index;

        // Check for merged rows or columns
        const isMergedInColumns =
          stopValue - stepValue >= startRow - range[1] &&
          seriesIn === "Columns" &&
          activeColumnId === mergedColumnId &&
          range[1] < startRow;

        const isMergedInRows =
          stopValue - stepValue >= startColumn - range[0] &&
          seriesIn === "Rows" &&
          activeRowId === mergedRowId &&
          range[0] < startColumn;

        return isMergedInColumns || isMergedInRows;
      });
    },
    calculateStopSize: (type, stepValue, stopValue, firstCellValue) => {
      return type === "Linear"
        ? Math.ceil((stopValue - firstCellValue + 1) / stepValue)
        : Math.floor(Math.log(stopValue - firstCellValue) / Math.log(stepValue));
    },
    calculateArraySize: (length, stopSize) => {
      return length <= 1 ? stopSize : stopSize <= 0 || isNaN(stopSize) ? length : Math.min(length, stopSize);
    },
    getSeriesData: (type, stepValue, stopValue, index, firstCellValue) => {
      return type === "Linear" ? firstCellValue + index * stepValue : firstCellValue * Math.pow(stepValue, index);
    },
    getNewRange: (range, seriesIn, arraySize) => {
      return [
        range[0],
        range[1],
        seriesIn === "Rows" ? range[0] + arraySize - 1 : range[2],
        seriesIn === "Columns" ? range[1] + arraySize - 1 : range[3],
      ];
    },
  };
};

const main = (context) => {
  const { dispatch, rootState, rootGetters, currentSheet, current } = context;
  return {
    set: async (initialRange, newRange) => {
      const fillHelper = new FillHelper(
        hfInstance,
        rootState,
        rootGetters,
        dispatch,
        currentSheet,
        initialRange,
        newRange
      );
      // initially we use hyperformulas range dragging capabilites. later we change series column or rows because hyperformula
      // doesn't support series copying
      let series = utils(context).setDefaultHfBehavior(initialRange, newRange);
      let contentReverseFunc = await Content(context).main.set(newRange, getMatrixOfValue(newRange, null));
      let result = hfInstance.setCellContents(
        { sheet: currentSheet.sheetId, col: newRange[0], row: newRange[1] },
        series
      );

      let styleReverseFunc = Style(context).main.copyStyleFromSourceToTarget(initialRange, newRange);

      const tempSeries = [...series];
      if (fillHelper.direction === "down" || fillHelper.direction === "up") {
        let j = 0;

        for (let i = newRange[0]; i <= newRange[2]; i++) {
          const baseRange = [i, initialRange[1], i, initialRange[3]];
          series = rootGetters["cells/getRangeData"]({ range: baseRange });
          const columnSeries = getColumnFromMatrix(tempSeries, j++);
          const isFormula = containsFormula(series);
          if (isFormula) {
            const rowNewRange = [i, newRange[1], i, newRange[3]];
            series = await fillHelper.getTableRefrenceSeries(baseRange, rowNewRange, series, columnSeries);
            result = hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: i, row: newRange[1] }, series);
          } else if (fillHelper.isNumberSeries(baseRange) && !fillHelper.shouldDuplicate()) {
            series = fillHelper.getNewSeries(baseRange);
            result = hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: i, row: newRange[1] }, series);
          } else if (fillHelper.isTextSeries(baseRange) && !fillHelper.shouldDuplicate()) {
            series = fillHelper.getNewTextSeries(baseRange);
            result = hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: i, row: newRange[1] }, series);
          }
        }
      } else {
        let j = 0;
        for (let i = newRange[1]; i <= newRange[3]; i++) {
          const baseRange = [initialRange[0], i, initialRange[2], i];
          const rowNewRange = [newRange[0], i, newRange[2], i];
          series = rootGetters["cells/getRangeData"]({ range: baseRange });
          const isFormula = containsFormula(series);
          if (isFormula) {
            series = await fillHelper.getTableRefrenceSeries(baseRange, rowNewRange, series, tempSeries[j++]);
            result = hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: newRange[0], row: i }, series);
          } else if (fillHelper.isNumberSeries(baseRange) && !fillHelper.shouldDuplicate()) {
            series = fillHelper.getNewSeries(baseRange);
          } else if (fillHelper.isTextSeries(baseRange) && !fillHelper.shouldDuplicate()) {
            series = fillHelper.getNewTextSeries(baseRange);
          }
          result = hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: newRange[0], row: i }, series);
        }
      }
      setTimeout(() => {
        dispatch("corners/setChangedCells", result, { root: true });
      }, 0);
      const targetCellIds = rootGetters["cells/rangeIds"](newRange);

      // update structured table.
      const activeTable = rootGetters["table/activeTable"](targetCellIds[0]);

      if (activeTable) {
        const updatedTableRange = unifyRanges(rangeLabelToIndex(activeTable.tableRange), newRange);
        const updatedTable = { ...activeTable, tableRange: rangeIndexToLabel(updatedTableRange) };
        dispatch("table/updateTableDetails", { updatedTable, oldTable: activeTable }, { root: true });
      }

      dispatch("views/updateViewData", targetCellIds, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });

      const valueMatrix = rootGetters["cells/getRangeData"](newRange);
      await dispatch("persistSelectedCells", { range: newRange, value: valueMatrix });

      // Reset range
      let activeCellId = rootState.cells.activeCellId;
      dispatch("cells/setFormulaRange", { title: rangeIndexToLabel(newRange), activeCellId }, { root: true });
      dispatch(
        "cells/setRangeDragging",
        {
          id: null,
          position: "",
          cursorType: "",
          range: null,
        },
        { root: true }
      );
      return async () => {
        await contentReverseFunc();
        styleReverseFunc();
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    series: async (range, seriesIn, type, dateUnit, trend, stepValue, stopValue) => {
      const initialRangeData = hfInstance.getRangeSerialized({
        start: { sheet: currentSheet.sheetId, col: range[0], row: range[1] },
        end: { sheet: currentSheet.sheetId, col: range[2], row: range[3] },
      });
      const rowLength = initialRangeData.length;
      const columnLength = initialRangeData[0].length;

      let arraySize, styleRange;
      // i for row j for column
      if (seriesIn === "Columns") {
        for (let j = 0; j < columnLength; j++) {
          const firstCellValue = initialRangeData[0][j];
          if (!firstCellValue || isNaN(firstCellValue)) continue;

          const stopSize = utils(context).calculateStopSize(type, stepValue, stopValue, firstCellValue);
          if (stopSize < 0 && stopValue !== undefined) continue;

          arraySize = utils(context).calculateArraySize(rowLength, stopSize);
          if (isNaN(arraySize) || arraySize == Infinity) continue;

          let array = new Array(arraySize)
            .fill(0)
            .map(
              (item, index) => utils(context).getSeriesData(type, stepValue, stopValue, index, +firstCellValue) + ""
            );
          array.forEach((item, index) => {
            if (!initialRangeData[index]) initialRangeData[index] = [];
            initialRangeData[index][j] = item;
          });
        }
        styleRange = [range[0], range[1], range[2], range[1]];
      } else {
        for (let i = 0; i < rowLength; i++) {
          const firstCellValue = initialRangeData[i][0];
          if (!firstCellValue || isNaN(firstCellValue)) continue;

          const stopSize = utils(context).calculateStopSize(type, stepValue, stopValue, firstCellValue);
          if (stopSize < 0 && stopValue !== undefined) continue;

          arraySize = utils(context).calculateArraySize(columnLength, stopSize);
          if (isNaN(arraySize) || arraySize == Infinity) continue;

          let array = new Array(arraySize)
            .fill(0)
            .map(
              (item, index) => utils(context).getSeriesData(type, stepValue, stopValue, index, +firstCellValue) + ""
            );
          initialRangeData[i] = array;
        }
        styleRange = [range[0], range[1], range[0], range[3]];
      }

      const newRange = utils(context).getNewRange(range, seriesIn, arraySize);

      const contentReverseFunc = await Content(context).main.set(newRange, initialRangeData);
      const styleReverseFunc = Style(context).main.copyStyleFromSourceToTarget(styleRange, newRange);

      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      await dispatch("persistSelectedCells", { range: newRange, value: initialRangeData });
      return async () => {
        await contentReverseFunc();
        styleReverseFunc();
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
  };
};
export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
